import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  Image as ImageIcon, 
  X,
  GripVertical,
  Eye
} from 'lucide-react';

/**
 * InspectionFilePreviewList - List of uploaded files with previews
 * 
 * Supports:
 * - Thumbnail preview
 * - Remove file
 * - Reorder (drag)
 */
export default function InspectionFilePreviewList({ 
  files, 
  onRemove, 
  onReorder 
}) {
  const [previews, setPreviews] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);
  
  // Generate previews for images
  useEffect(() => {
    const newPreviews = {};
    
    files.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviews(prev => ({
            ...prev,
            [index]: e.target.result
          }));
        };
        reader.readAsDataURL(file);
      }
    });
    
    // Cleanup old previews
    setPreviews(prev => {
      const cleaned = {};
      files.forEach((_, index) => {
        if (prev[index]) cleaned[index] = prev[index];
      });
      return cleaned;
    });
  }, [files]);
  
  // Get file icon based on type
  const getFileIcon = (file) => {
    if (file.type === 'application/pdf') {
      return <FileText className="h-8 w-8 text-red-500" />;
    }
    if (file.type.startsWith('image/')) {
      return <ImageIcon className="h-8 w-8 text-blue-500" />;
    }
    return <FileText className="h-8 w-8 text-gray-500" />;
  };
  
  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Drag handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      onReorder(draggedIndex, index);
      setDraggedIndex(index);
    }
  };
  
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };
  
  if (files.length === 0) return null;
  
  return (
    <div className="space-y-2" data-testid="file-preview-list">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">
          Uploaded Files ({files.length})
        </h4>
        <span className="text-xs text-gray-500">
          Drag to reorder
        </span>
      </div>
      
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {files.map((file, index) => (
          <div
            key={`${file.name}-${index}`}
            className={`
              flex items-center gap-3 p-2 bg-gray-50 rounded-lg border
              ${draggedIndex === index ? 'border-blue-400 bg-blue-50' : 'border-transparent'}
              hover:border-gray-200 transition-colors
            `}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            data-testid={`file-item-${index}`}
          >
            {/* Drag handle */}
            <div className="cursor-move text-gray-400 hover:text-gray-600">
              <GripVertical className="h-4 w-4" />
            </div>
            
            {/* Preview/Icon */}
            <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-white border">
              {previews[index] ? (
                <img 
                  src={previews[index]} 
                  alt={file.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  {getFileIcon(file)}
                </div>
              )}
            </div>
            
            {/* File info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {file.name}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className="text-xs">
                  {file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                </Badge>
                <span className="text-xs text-gray-500">
                  {formatSize(file.size)}
                </span>
              </div>
            </div>
            
            {/* Position badge */}
            <Badge variant="secondary" className="text-xs">
              #{index + 1}
            </Badge>
            
            {/* Remove button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(index)}
              className="text-gray-400 hover:text-red-500"
              data-testid={`remove-file-${index}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
